// Bridges the form state ↔ the composed `{elements, theme}` used by the live
// preview and the final created project. Deterministic: no AI calls after the draft.
import { SECTION_TONE_STYLE, PORTRAIT_CATEGORIES, businessPortraitIn, placeBusinessPortrait, applySectionCopy, fillLocations, normalizeLocations, type BusinessLocation, bindArticlesToBlog, fillBusinessHours, fillContactCards, inferTemplateSlots, normalizeBusinessHours, type BusinessHoursRow, markContactCards, markContactSlots, sectionToneColorRef, targetOffersKind, withSectionTone, type CopyNode } from '@webbuilder/schemas'
import { DEFAULT_PAGE_THEME, DESIGN_STYLES, STYLE_PALETTE_PREFIX, applyStyleRecipe, harmonizeRecipeColor, motionStyleForDesign, normalizeProjectTheme, paletteById, paletteWithCustomColors, paletteWithHue, recipeFromTheme, type CustomPalette, type ProjectTheme as ThemeProjectTheme, type SiteMotionStyle, type StyleRecipe } from '@webbuilder/theme'
import type { AiDraft, AiDraftSection, DemoPage, DemoSite } from '../api'
import type { ElementNode, ProjectTheme } from './types'
import { translate } from '../i18n'
import { wireLinks } from './wireLinks'


export type SectionMeta = { id: string; label: string }

/**
 * Cada forma de contacto que se puede añadir en el último paso.
 *
 * No hay campos fijos: se añade sólo lo que se tiene —un correo y un Instagram,
 * o un WhatsApp y nada más—, y todo lo que se añade sale en el pie del sitio.
 */
export const CONTACT_KINDS = ['email', 'phone', 'whatsapp', 'address', 'instagram', 'facebook', 'linkedin', 'tiktok', 'x', 'youtube', 'website'] as const
export type ContactKind = (typeof CONTACT_KINDS)[number]
export type ContactEntry = { id: string; kind: ContactKind; value: string }

/** Las redes: su nombre en el pie y la dirección a la que va un @usuario. */
const SOCIAL_NETWORKS: Partial<Record<ContactKind, { label: string; base: string }>> = {
  instagram: { label: 'Instagram', base: 'https://instagram.com/' },
  facebook: { label: 'Facebook', base: 'https://facebook.com/' },
  linkedin: { label: 'LinkedIn', base: 'https://linkedin.com/in/' },
  tiktok: { label: 'TikTok', base: 'https://tiktok.com/@' },
  x: { label: 'X', base: 'https://x.com/' },
  youtube: { label: 'YouTube', base: 'https://youtube.com/@' },
}

/** Una dirección a partir de lo escrito: una URL tal cual, un @usuario con la base de su red. */
function socialUrl(kind: ContactKind, raw: string): string {
  const value = raw.trim()
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  /* Un dominio sin «https://». En una red sólo si lleva ruta: «cafe.luna» es
     un usuario de Instagram válido, no una web. */
  if ((kind === 'website' || value.includes('/')) && /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/|$)/i.test(value)) return `https://${value}`
  const network = SOCIAL_NETWORKS[kind]
  return network ? network.base + value.replace(/^@/, '') : ''
}

/** Lo que va a `projectContent`: el primer correo, teléfono y dirección, y las redes. */
export function contactsToContent(entries: ContactEntry[]) {
  const first = (kind: ContactKind) => entries.find((entry) => entry.kind === kind && entry.value.trim())?.value.trim() ?? ''
  const email = entries.map((entry) => (entry.kind === 'email' ? entry.value.trim() : '')).find((value) => isValidEmail(value)) ?? ''
  const socials: { label: string; url: string }[] = []
  for (const entry of entries) {
    const value = entry.value.trim()
    if (!value) continue
    if (entry.kind === 'whatsapp') {
      const digits = value.replace(/[^\d]/g, '')
      if (digits) socials.push({ label: 'WhatsApp', url: `https://wa.me/${digits}` })
    } else if (entry.kind === 'website') {
      const url = socialUrl(entry.kind, value)
      if (url) socials.push({ label: url.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, ''), url })
    } else if (SOCIAL_NETWORKS[entry.kind]) {
      const url = socialUrl(entry.kind, value)
      if (url) socials.push({ label: SOCIAL_NETWORKS[entry.kind]!.label, url })
    }
  }
  return { email, phone: first('phone'), address: first('address'), socials }
}

export function slugify(name: string): string {
  return (name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 72)) || 'pagina-ai'
}

export type CreateForm = {
  pageName: string
  slug: string
  /** True once the user edits the slug by hand — stops auto-deriving it from the name. */
  slugTouched: boolean
  companyName: string
  logoUrl: string
  /**
   * Contacto y redes del negocio, sólo los que se añadieron: van al pie, a
   * «Información del negocio» y a las políticas. Opcionales todos.
   */
  contacts: ContactEntry[]
  /** El horario del negocio: va al mapa, al contacto y al pie. Vacío = sin horario. */
  hours: BusinessHoursRow[]
  /** Las sucursales, sólo si son dos o más (una es la dirección de contacto). */
  locations: BusinessLocation[]
  /**
   * El retrato del negocio que eligió el worker (`null`: ninguno, se quita).
   * Sin el dato —un borrador de antes—, se usa el de la variante anterior.
   */
  portrait?: string | null | undefined
  /** El estilo del sitio: uno de los del panel con lo que se cambie encima. */
  recipe: StyleRecipe
  /**
   * La receta tal como la dejó la IA, para la tarjeta «Elegida por la IA» del
   * paso «Combinación» y poder volver a ella. Los borradores de antes no la traen.
   */
  aiRecipe?: StyleRecipe | undefined
  /**
   * La combinación de partida elegida en el paso «Combinación»: el id de un
   * estilo de diseño, o nada = la de la IA. Sólo marca la tarjeta: lo que se
   * cambie después en colores, letra o estilo va en `recipe`.
   */
  combination?: string | undefined
  /**
   * El movimiento del sitio, si se eligió en el paso «Animación». Sin él, el
   * que va con el estilo (`suggestedMotion`). Va al tema al crear (`brandedTheme`):
   * rehacer el tema desde la receta no lo trae.
   */
  motionStyle?: SiteMotionStyle | undefined
  /** La paleta que eligió la IA: se ofrece primero en el paso de colores. */
  aiPaletteId?: string | undefined
  /**
   * «Tu paleta»: los colores que usará el sitio, por papel. Arrancan con los de
   * la IA (o los que dio el cliente) y se editan uno a uno; al elegir otra
   * paleta en el paso de colores, sus colores pasan aquí para ajustarlos.
   */
  brandPalette: CustomPalette
  /**
   * La paleta tal como la dejó la IA, para ofrecerla primera («Creada por la
   * IA») y poder volver a ella después de probar otras o de editar. Los
   * borradores de antes no la traen: se rehace de `aiPaletteId`.
   */
  aiPalette?: CustomPalette | undefined
  theme: ProjectTheme
  /** Las secciones de la portada, para reordenarlas y cambiarlas de variante sin IA. */
  sections: AiDraftSection[]
  /**
   * Las demás páginas, con lo mismo para cada una (ver `FormPage`). Opcional:
   * los borradores de antes no lo traen y se leen de `base` (`extraPagesOf`).
   */
  extraPages?: FormPage[] | undefined
  /** Precios, testimonios o cifras: son ejemplos, hay que revisarlos. */
  exampleContent: boolean
  /** Lo que el negocio necesita además de páginas (reservas, tienda…). */
  features: string[]
  /** Lo que la IA dejó fuera por los límites del plan. */
  trimmed: { pages: number; sections: number }
  /** El idioma en que se escribió el sitio. */
  language: 'es' | 'en'
  /** Original AI elements — immutable base for diffing/text/section edits. */
  baseElements: ElementNode[]
  /** Section ids turned off (everything on by default). */
  disabledSections: string[]
  /** sectionId → override image url. */
  sectionImages: Record<string, string>
  /** elementId → edited text content (overrides the AI copy in the preview). */
  textEdits: Record<string, string>
  /** Original AI business name, used to retext to companyName in the preview. */
  aiBusinessName: string
  /** Base project payload (pages/projectContent) to merge on create. */
  base: AiDraft['project']
  /**
   * El tema de partida cuando se empieza desde un template de sitio: sus
   * colores, su letra y su estilo. Sobre él se aplica la receta; sin él (IA),
   * sobre el tema por defecto, como lo hizo el generador.
   */
  baseTheme?: ProjectTheme | undefined
  /** El template de sitio del que sale, si sale de uno. */
  templateId?: string | undefined
}

/**
 * Una página del sitio que no es la portada, con lo mismo que la portada para
 * el paso de secciones: sus nodos, sus secciones y las que se apagaron. Y si
 * se crea: el interruptor «Incluir esta página».
 */
export type FormPage = {
  /** Su posición en `base.pages` (la 0 es la portada, que nunca va aquí). */
  index: number
  name: string
  slug: string
  baseElements: ElementNode[]
  sections: AiDraftSection[]
  disabledSections: string[]
  included: boolean
}

/** Lo de una página que toca el paso de secciones. */
export type PageSlice = Pick<CreateForm, 'baseElements' | 'sections' | 'disabledSections'>

/** Las páginas además de la portada. Los borradores de antes no las traen: salen de `base`, sin datos de secciones. */
export function extraPagesOf(form: CreateForm): FormPage[] {
  if (form.extraPages) return form.extraPages
  return form.base.pages.slice(1).map((page, at) => ({
    index: at + 1,
    name: typeof page.name === 'string' ? page.name : '',
    slug: typeof page.slug === 'string' ? page.slug : '',
    baseElements: (page.elements ?? []) as ElementNode[],
    sections: [],
    disabledSections: [],
    included: true,
  }))
}

/** Lo de la página `page` (0 = la portada). */
export function pageSlice(form: CreateForm, page = 0): PageSlice {
  if (page === 0) return { baseElements: form.baseElements, sections: form.sections, disabledSections: form.disabledSections }
  const found = extraPagesOf(form).find((item) => item.index === page)
  return found ?? { baseElements: [], sections: [], disabledSections: [] }
}

/** El formulario con lo de la página `page` cambiado. */
export function withPageSlice(form: CreateForm, page: number, slice: PageSlice): CreateForm {
  if (page === 0) return { ...form, ...slice }
  return { ...form, extraPages: extraPagesOf(form).map((item) => (item.index === page ? { ...item, ...slice } : item)) }
}

/** Incluir una página o dejarla fuera del sitio. La portada siempre va. */
export function setPageIncluded(form: CreateForm, page: number, included: boolean): CreateForm {
  if (page === 0) return form
  return { ...form, extraPages: extraPagesOf(form).map((item) => (item.index === page ? { ...item, included } : item)) }
}

// ─── element tree helpers ─────────────────────────────────────────────────────

function mapTree(nodes: ElementNode[], fn: (n: ElementNode) => ElementNode): ElementNode[] {
  return nodes.map((n) => {
    const next = fn(n)
    return next.children?.length ? { ...next, children: mapTree(next.children, fn) } : next
  })
}


/** Top-level sections, labelled from sectionType / nav label / first heading text. */
export function getSections(elements: ElementNode[]): SectionMeta[] {
  return elements.map((node, i) => ({ id: node.id, label: sectionLabel(node, i) }))
}

/*
 * Cómo se llama cada sección en pantalla, deducido por palabras clave.
 * El orden importa: primero lo más específico.
 *
 * Guarda CLAVES del catálogo y no el rótulo ya escrito. Es una constante de
 * módulo —no puede llamar a un hook— y lo que devuelve se pinta tal cual, así
 * que con el texto aquí el resumen del asistente seguiría en español en un panel
 * en inglés. Las palabras clave de la izquierda NO se traducen: son lo que trae
 * el documento, y ahí conviven las inglesas y las españolas a propósito.
 */
const LABEL_RULES: { keys: string[]; labelKey: string }[] = [
  { keys: ['header', 'navbar', 'nav', 'menu'], labelKey: 'section.header' },
  { keys: ['hero', 'banner', 'intro', 'masthead'], labelKey: 'section.hero' },
  { keys: ['feature', 'service', 'benefit', 'card', 'grid', 'highlight'], labelKey: 'section.cards' },
  { keys: ['testimonial', 'review', 'opinion', 'quote'], labelKey: 'section.testimonials' },
  { keys: ['faq', 'question', 'pregunta', 'accordion'], labelKey: 'section.faq' },
  { keys: ['pricing', 'price', 'precio', 'plan'], labelKey: 'section.pricing' },
  { keys: ['gallery', 'galer', 'portfolio', 'showcase'], labelKey: 'section.gallery' },
  { keys: ['team', 'equipo', 'staff'], labelKey: 'section.team' },
  { keys: ['stat', 'metric', 'number', 'cifra'], labelKey: 'section.stats' },
  { keys: ['logo', 'brand', 'client', 'partner'], labelKey: 'section.logos' },
  { keys: ['about', 'nosotros', 'story', 'historia'], labelKey: 'section.about' },
  { keys: ['cta', 'call-to', 'newsletter', 'subscribe', 'signup'], labelKey: 'section.cta' },
  { keys: ['contact', 'contacto', 'form'], labelKey: 'section.contact' },
  { keys: ['footer', 'pie'], labelKey: 'section.footer' },
]

function matchLabel(hay: string): string | null {
  for (const rule of LABEL_RULES) if (rule.keys.some((k) => hay.includes(k))) return translate(rule.labelKey)
  return null
}

function sectionLabel(node: ElementNode, index: number): string {
  // 1) By tagged type / schema id.
  const tagged = `${node.sectionType ?? ''} ${node.schemaId ?? ''}`.toLowerCase()
  const byType = matchLabel(tagged)
  if (byType) return byType
  // 2) By structure — never use the raw heading text (it produces odd names).
  if (index === 0) return translate('section.hero')
  if (hasCardGrid(node)) return translate('section.cards')
  if (countImages(node) >= 2) return translate('section.gallery')
  return translate('section.numbered', { n: index + 1 })
}

function hasCardGrid(node: ElementNode): boolean {
  const walk = (n: ElementNode): boolean => {
    const kids = n.children ?? []
    const divKids = kids.filter((c) => c.type === 'div' && (c.children?.length ?? 0) > 0)
    if (divKids.length >= 2) return true
    return kids.some(walk)
  }
  return walk(node)
}

function countImages(node: ElementNode): number {
  let n = node.type === 'image' ? 1 : 0
  for (const c of node.children ?? []) n += countImages(c)
  return n
}

// ─── compose ──────────────────────────────────────────────────────────────────

function retext(content: string | undefined, from: string, to: string): string | undefined {
  if (!content || !from || !to || from === to) return content
  return content.split(from).join(to)
}

/**
 * El fondo alterno de cada sección según el orden en que queda: una sí y otra
 * no, como lo puso el generador (`applySectionTone` en el worker). Al mover o
 * apagar secciones se recalcula; si no, dos seguidas salían del mismo color y
 * no se veía dónde acababa una y empezaba la otra.
 */
function sectionTones(slice: PageSlice, disabled: Set<string>): Map<string, number> {
  const tones = new Map<string, number>()
  let order = 0
  for (const section of slice.sections) {
    const first = section.rootIds.find((id) => !disabled.has(id))
    if (first) tones.set(first, order++)
  }
  return tones
}

/**
 * Sólo las que traen el fondo alterno del generador: un fondo elegido a
 * propósito se respeta. Con su estilo de sección, como el generador (ver
 * `withSectionTone`); un borrador de antes que lo traía con la referencia de
 * color suelta sale también así.
 */
function withTone(node: ElementNode, order: number | undefined): ElementNode {
  if (order === undefined || (node.backgroundRole !== 'background' && node.backgroundRole !== 'surface')) return node
  const secondary = order % 2 === 1
  const section = node.styleRefs?.section
  const done = node.backgroundRole === (secondary ? 'surface' : 'background')
    && !sectionToneColorRef(node)
    && (!section || section === (secondary ? SECTION_TONE_STYLE.secondary : SECTION_TONE_STYLE.default))
  return done ? node : withSectionTone(node, secondary)
}

/** Build the live elements from the immutable base + form edits. */
export function composeElements(form: CreateForm, slice: PageSlice = form): ElementNode[] {
  const disabled = new Set(slice.disabledSections)
  const kept = slice.baseElements.filter((s) => !disabled.has(s.id))
  const tones = sectionTones(slice, disabled)
  return kept.map((original) => {
    const section = withTone(original, tones.get(original.id))
    let imageApplied = false
    const override = form.sectionImages[section.id]
    const next = mapTree([section], (n) => {
      const patch: Partial<ElementNode> = {}
      // Explicit text edit wins; otherwise retext the AI business name → company name.
      if (n.content) {
        const edited = form.textEdits[n.id]
        if (edited !== undefined && edited !== n.content) {
          patch.content = edited
        } else {
          const c = retext(n.content, form.aiBusinessName, form.companyName)
          if (c && c !== n.content) patch.content = c
        }
      }
      // First image of the section gets the override photo.
      if (override && !imageApplied && n.type === 'image') {
        imageApplied = true
        patch.src = override
        /* Una foto de fondo con pase de fotos (slider): sus otras fotos eran las
           de antes y seguían pasando. Con la elegida, ésa sola. */
        if ((n as ElementNode & { backgroundSlider?: unknown }).backgroundSlider) (patch as Record<string, unknown>).backgroundSlider = undefined
      }
      return Object.keys(patch).length ? { ...n, ...patch } : n
    })
    return next[0]!
  })
}

/**
 * El movimiento que va con el sitio sin elegir otro: el del template si trae
 * uno; si no, el que va con su estilo visual, como lo elige la IA
 * (`motionStyleForDesign`). Sigue a la combinación mientras no se elija otro.
 */
export function suggestedMotion(form: CreateForm): SiteMotionStyle {
  const fromTemplate = (form.baseTheme as { motion?: { style?: SiteMotionStyle } } | undefined)?.motion?.style
  return fromTemplate ?? motionStyleForDesign(form.recipe.designStyleId)
}

/** El movimiento con que se crea el sitio. */
export function siteMotionOf(form: CreateForm): SiteMotionStyle {
  return form.motionStyle ?? suggestedMotion(form)
}

/** Final project `data` for createProject — merges edits into the AI base payload. */
/**
 * El tema con la marca del usuario: su nombre en el logo y, si subió uno, su
 * imagen. Es el token global de logo, así que sale en la barra, en el pie y en
 * todo lo que lo use, y se sigue cambiando en Style › Logo.
 */
function brandedTheme(form: CreateForm): ProjectTheme {
  const theme = form.theme as ProjectTheme & { brandLogo?: Record<string, unknown>; motion?: Record<string, unknown> }
  /* Y con su movimiento (Marca › Animación): el tema del asistente se rehace
     desde la receta en cada cambio y no lo trae. */
  const motion = { ...(theme.motion ?? {}), style: siteMotionOf(form) }
  /* Un proyecto nuevo nace con «Alternar fondos» encendido (`sectionToneAuto`). */
  if (!theme.brandLogo) return { ...form.theme, sectionToneAuto: true, motion } as ProjectTheme
  return {
    ...theme,
    sectionToneAuto: true,
    motion,
    brandLogo: {
      ...theme.brandLogo,
      name: form.companyName || theme.brandLogo.name,
      ...(form.logoUrl ? { mark: 'image', imageLight: form.logoUrl } : {}),
    },
  } as ProjectTheme
}

/**
 * Los datos de contacto de muestra de un template («(555) 123-4567»,
 * «hello@yourbusiness.com»), marcados como lo hace el worker al crear con IA:
 * los textos con hueco de contacto en cualquier sección y las tarjetas de las
 * secciones de contacto. Sin datos todavía, todos salen «por completar», y el
 * paso de contacto los rellena (`withContactCards`). Muta los nodos: son las
 * copias recién hechas por `remapIds`.
 */
function markTemplateContacts(elements: ElementNode[], language: 'es' | 'en') {
  for (const section of elements) {
    const nodes = [section] as unknown as CopyNode[]
    markContactSlots(nodes, [], { language })
    const hay = `${(section as { label?: string }).label ?? ''} ${(section as { anchorId?: string }).anchorId ?? ''} ${(section as { navLabel?: string }).navLabel ?? ''}`.toLowerCase()
    if (hay.includes('contact')) markContactCards(nodes, [], { language })
  }
}

/**
 * El formulario del asistente a partir de un template de sitio, sin IA.
 *
 * Los mismos pasos que con IA —nombre, colores, letra, estilo, contacto—, con
 * lo del template como punto de partida: su página, su tema y su nombre de
 * marca (que se cambia por el del cliente en los textos y el logo). «Tu
 * paleta» y la letra son las del template, así que elegir un estilo cambia la
 * forma sin tocarlas (ver `recipeFromTheme`).
 */
export function hydrateFromTemplate(input: { templateId: string; elements: ElementNode[]; theme: unknown; language: 'es' | 'en'; homeName: string; keepIds?: boolean }): CreateForm {
  const baseTheme = normalizeProjectTheme((input.theme ?? DEFAULT_PAGE_THEME) as never) as unknown as ProjectTheme
  const recipe = recipeFromTheme(baseTheme as unknown as ThemeProjectTheme)
  const brandName = String((baseTheme as unknown as { brandLogo?: { name?: string } }).brandLogo?.name ?? '')
  /* `keepIds`: la página de veltica.com, cuyos textos escritos sobre la página
     publicada llegan por el id de su nodo (ver `hydrateFromSite`). */
  const elements = input.elements.map(input.keepIds ? withChildren : remapIds)
  if (!input.keepIds) markTemplateContacts(elements, input.language)
  return {
    pageName: '',
    slug: '',
    slugTouched: false,
    companyName: '',
    logoUrl: '',
    contacts: [],
    hours: [],
    locations: [],
    recipe,
    brandPalette: recipe.palette ?? {},
    theme: baseTheme,
    baseTheme,
    templateId: input.templateId,
    sections: [],
    exampleContent: false,
    features: [],
    trimmed: { pages: 0, sections: 0 },
    language: input.language,
    baseElements: elements,
    disabledSections: [],
    sectionImages: {},
    textEdits: {},
    aiBusinessName: brandName,
    base: {
      pages: [{
        id: crypto.randomUUID(),
        name: input.homeName,
        slug: '/',
        isHome: true,
        elements,
        pageStyles: { desktop: { overflow: 'hidden', overflowY: 'visible' } },
      }],
      projectTheme: baseTheme as unknown as Record<string, unknown>,
      projectContent: {},
    },
  }
}

/**
 * El formulario de la demo pública (`/demo`), sin IA y sin cuenta.
 *
 * Cada hueco de la página es su bloque con los textos de venta repartidos en
 * sus slots, igual que hace «Otra variante» (`buildVariant`): por eso las
 * variantes se cambian con el mismo paso de Secciones del asistente. El estilo
 * de partida es `recipe`, y va también como `aiRecipe` para que la primera
 * tarjeta de «Combinación» devuelva a él.
 *
 * El nombre de la marca es el de véltica; al llevarse el diseño, el nombre
 * que escriba el cliente lo cambia en el logo y en los textos que lo nombran
 * (`retext`), como en cualquier otro camino del asistente.
 */
export function hydrateFromDemo(demo: DemoPage, input: { homeName: string; recipe: StyleRecipe }): CreateForm {
  const elements: ElementNode[] = []
  const sections: AiDraftSection[] = []
  for (const slot of demo.slots) {
    const nodes = inferTemplateSlots(slot.elements as ElementNode[], slot.category).elements.map(cloneNode)
    applySectionCopy(nodes as unknown as CopyNode[], slot.copy, (node) => cloneNode(node as unknown as ElementNode) as unknown as CopyNode)
    if (!nodes.length) continue
    const [first, ...rest] = slot.variants
    if (!first) continue
    elements.push(...nodes)
    sections.push({
      rootIds: nodes.map((node) => node.id),
      category: slot.category,
      templateId: first.id,
      thumbnailUrl: first.thumbnailUrl,
      copy: slot.copy,
      alternatives: rest,
      variants: slot.variants,
    })
  }
  const defaults = normalizeProjectTheme(DEFAULT_PAGE_THEME) as unknown as ThemeProjectTheme & { brandLogo?: Record<string, unknown> }
  const branded = { ...defaults, brandLogo: { ...(defaults.brandLogo ?? {}), name: demo.brandName } } as unknown as ProjectTheme
  const draft: CreateForm = {
    pageName: '',
    slug: '',
    slugTouched: false,
    companyName: demo.brandName,
    logoUrl: '',
    contacts: [],
    hours: [],
    locations: [],
    recipe: input.recipe,
    aiRecipe: input.recipe,
    aiPaletteId: input.recipe.paletteId,
    brandPalette: brandPaletteOf(input.recipe),
    aiPalette: brandPaletteOf(input.recipe),
    theme: branded,
    sections,
    exampleContent: false,
    features: [],
    trimmed: { pages: 0, sections: 0 },
    language: demo.language,
    baseElements: elements,
    disabledSections: [],
    sectionImages: {},
    textEdits: {},
    aiBusinessName: demo.brandName,
    base: {
      pages: [{
        id: crypto.randomUUID(),
        name: input.homeName,
        slug: '/',
        isHome: true,
        elements,
        pageStyles: { desktop: { overflow: 'hidden', overflowY: 'visible' } },
      }],
      projectTheme: branded as unknown as Record<string, unknown>,
      projectContent: {},
    },
  }
  return { ...draft, theme: themeForRecipe(draft, input.recipe) }
}

/**
 * El formulario de la demo con la portada de veltica.com (`source=site`): la
 * página que se está viendo, con su tema, su barra y su pie. Como un template
 * de sitio (`hydrateFromTemplate`), con su estilo como punto de partida
 * (`aiRecipe`, para volver a él desde «Combinación») y su nombre de marca, que
 * se cambia por el del cliente al llevársela. Sin variantes de sección: la
 * página es una sola, hecha a mano.
 */
export function hydrateFromSite(site: DemoSite, homeName: string): CreateForm {
  const elements = Array.isArray(site.page.elements) ? site.page.elements as ElementNode[] : []
  const form = hydrateFromTemplate({ templateId: '', elements, theme: site.projectTheme, language: site.language, homeName, keepIds: true })
  const page = form.base.pages[0] ?? {}
  return {
    ...form,
    templateId: undefined,
    aiRecipe: form.recipe,
    companyName: site.brandName,
    aiBusinessName: site.brandName,
    base: {
      ...form.base,
      pages: [{ ...page, ...(site.page.pageStyles ? { pageStyles: site.page.pageStyles } : {}) }],
      projectContent: (site.projectContent ?? {}) as Record<string, unknown>,
    },
  }
}

/** Los contactos que sacó la IA del pedido, como filas del paso de contacto. */
function aiContacts(draft: AiDraft): ContactEntry[] | null {
  const list = draft.project.aiGeneration?.contacts
  if (!Array.isArray(list) || !list.length) return null
  const kinds = new Set<string>(CONTACT_KINDS)
  /* Un teléfono o WhatsApp sin un solo dígito no es un dato: la IA a veces
     pone «WhatsApp» como valor. Llega vacío, para que el paso de contacto lo
     marque «por completar» en vez de enseñar la palabra. */
  const usable = (kind: string, value: string) => ((kind === 'phone' || kind === 'whatsapp') && !/\d/.test(value) ? '' : value)
  return list
    .filter((item) => kinds.has(item.kind) && typeof item.value === 'string' && item.value.trim())
    .map((item, index) => ({ id: `ai-${item.kind}-${index}`, kind: item.kind as ContactKind, value: usable(item.kind, item.value.trim()) }))
}

/** Las filas de horario completas (una a medio escribir no cuenta todavía). Un borrador viejo no trae horario. */
function completeHours(form: CreateForm): BusinessHoursRow[] {
  return (form.hours ?? []).filter((row) => row.days.trim() && row.time.trim()).map((row) => ({ days: row.days.trim(), time: row.time.trim() }))
}

/**
 * Las tarjetas de contacto con lo que hay en el paso de contacto: las que
 * marcó el worker (\`markContactCards\`) se rellenan otra vez; las que se quedan
 * sin dato, «por completar». Ver \`fillContactCards\`.
 */
function withContactCards(nodes: ElementNode[], form: CreateForm): ElementNode[] {
  const filled = fillContactCards(nodes as unknown as CopyNode[], form.contacts, { language: form.language }) as unknown as ElementNode[]
  /* Y el horario del paso de contacto en el de muestra de los bloques. */
  /* Un borrador guardado antes de que hubiera horario no lo trae. */
  const hours = completeHours(form)
  const withHours = hours.length ? fillBusinessHours(structuredClone(filled) as unknown as CopyNode[], hours) as unknown as ElementNode[] : filled
  /* Y las sucursales en el bloque de varias que marcó el worker. */
  const locations = completeLocations(form)
  if (locations.length < 2) return withHours
  return withHours.map((node) => ((node as ElementNode & { aiLocations?: boolean }).aiLocations
    ? fillLocations([cloneNode(node)] as unknown as CopyNode[], locations, (copy) => cloneNode(copy as unknown as ElementNode) as unknown as CopyNode)[0] as unknown as ElementNode
    : node))
}

/** Las sucursales completas (con dirección) del paso de contacto. */
export function completeLocations(form: CreateForm): BusinessLocation[] {
  return normalizeLocations(form.locations ?? [])
}

/** Las sucursales que sacó la IA del pedido: sólo si son dos o más. */
function aiLocations(draft: AiDraft): BusinessLocation[] {
  const locations = normalizeLocations(draft.project.aiGeneration?.locations)
  return locations.length > 1 ? locations : []
}

/** Final project `data` for createProject — merges edits into the AI base payload. */
export function composeProjectData(form: CreateForm) {
  const elements = withContactCards(composeElements(form), form)
  const theme = brandedTheme(form)
  const basePage = form.base.pages[0] ?? {}
  const baseContent = form.base.projectContent ?? {}
  const baseBusiness = (baseContent.business ?? {}) as Record<string, unknown>
  const name = form.companyName || form.aiBusinessName
  const contact = contactsToContent(form.contacts)
  return {
    ...form.base,
    pages: [
      {
        ...basePage,
        // El que puso el generador, en el idioma del sitio.
        name: typeof basePage.name === 'string' && basePage.name ? basePage.name : 'Inicio',
        elements,
        theme,
        seoTitle: name,
        ogTitle: name,
      },
      // Las demás páginas, las que se incluyen, con lo que se cambió en ellas.
      ...extraPagesOf(form).filter((page) => page.included).map((page) => composeExtraPage(form, page, theme, name)),
    ].map((page) => withoutLinksTo(page, excludedSlugs(form)))
      /* Con los datos de contacto ya finales: los botones de WhatsApp, llamar,
         correo y mapa, y los que no llevaban a nada (ver `wireLinks`). */
      .map((page, _index, all) => withWiredLinks(page, all, form)),
    projectTheme: theme,
    projectContent: {
      ...baseContent,
      business: { ...baseBusiness, name, logo: form.logoUrl, ...(completeHours(form).length ? { hours: completeHours(form) } : { hours: undefined }), ...(completeLocations(form).length > 1 ? { locations: completeLocations(form) } : { locations: undefined }) },
      contact: { email: contact.email, phone: contact.phone, address: contact.address },
      socials: contact.socials,
      ...floatingContactFor(form, baseContent),
    },
  }
}

/**
 * El botón flotante de contacto, encendido solo si dio WhatsApp o teléfono:
 * en un negocio local es lo que más se usa desde el celular («¿Te quedaste
 * tirado? Llámanos»). Si el proyecto ya trae uno, se respeta.
 */
function floatingContactFor(form: CreateForm, base: Record<string, unknown>): Record<string, unknown> {
  if (base.contactFloat) return {}
  const value = (kind: ContactKind) => form.contacts.find((entry) => entry.kind === kind && entry.value.trim())?.value.trim() ?? ''
  const whatsapp = value('whatsapp')
  const phone = value('phone')
  if (!whatsapp && !phone) return {}
  return {
    contactFloat: {
      enabled: true,
      whatsapp,
      phone,
      email: value('email'),
      form: true,
      formId: crypto.randomUUID(),
      side: 'right',
      greeting: '',
    },
  }
}

/** Una página que no es la portada, tal como se crea. */
function composeExtraPage(form: CreateForm, page: FormPage, theme: ProjectTheme, siteName: string): Record<string, unknown> {
  const base = form.base.pages[page.index] ?? {}
  return {
    ...base,
    theme,
    elements: withContactCards(composeElements(form, page), form),
    seoTitle: page.name ? `${page.name} · ${siteName}` : siteName,
  }
}

/**
 * Una página que no se incluye, como página de la vista previa: para verla
 * antes de decidir. No va en el proyecto (ni en su barra).
 */
export function composePreviewPage(form: CreateForm, page: number): Record<string, unknown> | null {
  const found = extraPagesOf(form).find((item) => item.index === page)
  if (!found) return null
  const name = form.companyName || form.aiBusinessName
  return composeExtraPage(form, found, brandedTheme(form), name)
}

/** Los slugs de las páginas que no se incluyen. */
function excludedSlugs(form: CreateForm): Set<string> {
  return new Set(extraPagesOf(form).filter((page) => !page.included && page.slug).map((page) => page.slug))
}

function withWiredLinks(page: Record<string, unknown>, all: Record<string, unknown>[], form: CreateForm): Record<string, unknown> {
  if (!Array.isArray(page.elements)) return page
  const value = (kind: ContactKind) => form.contacts.find((entry) => entry.kind === kind && entry.value.trim())?.value.trim() ?? ''
  const contact = contactsToContent(form.contacts)
  const slugOf = (candidate: Record<string, unknown>) => (typeof candidate.slug === 'string' ? candidate.slug : '')
  const contactPath = all.map(slugOf).find((slug) => /^\/?(contact|contacto)\b/i.test(slug)) ?? null
  const elements = wireLinks(page.elements as ElementNode[], {
    contacts: { whatsapp: value('whatsapp'), phone: contact.phone, email: contact.email, address: contact.address },
    contactPath: contactPath && !contactPath.startsWith('/') ? `/${contactPath}` : contactPath,
  }, slugOf(page).startsWith('/') || !slugOf(page) ? slugOf(page) : `/${slugOf(page)}`)
  return elements === page.elements ? page : { ...page, elements }
}

/**
 * Los botones que llevaban a una página que se quitó van a la portada: si no,
 * llevarían a una página que no existe.
 */
function withoutLinksTo(page: Record<string, unknown>, slugs: Set<string>): Record<string, unknown> {
  if (!slugs.size || !Array.isArray(page.elements)) return page
  const pathOf = (href: string) => href.split(/[?#]/)[0] ?? ''
  const elements = mapTree(page.elements as ElementNode[], (node) => {
    const href = node.interaction?.href
    return href && slugs.has(pathOf(href)) ? { ...node, interaction: { ...node.interaction, action: 'link', href: '/' } } : node
  })
  return { ...page, elements }
}

/** Lo del negocio que va a «Información del negocio» al crear: sólo lo que se llenó. */
/** Un correo con forma de correo; lo demás no se manda (el worker también lo ignoraría). */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())
}

export function composeBusinessInfo(form: CreateForm) {
  const contact = contactsToContent(form.contacts)
  const info: Record<string, string> = {
    tradeName: form.companyName.trim(),
    email: contact.email,
    phone: contact.phone,
    address: contact.address,
  }
  return Object.fromEntries(Object.entries(info).filter(([, value]) => value))
}

// ─── hydrate from AI draft ──────────────────────────────────────────────────────

/**
 * La receta de la IA con la paleta anclada: si no eligió una, la del estilo,
 * para que cambiar de estilo en el asistente no cambie también los colores. Un
 * color suelto —borradores de antes— se convierte en paleta con su tono.
 */
function anchoredRecipe(raw: StyleRecipe | undefined): StyleRecipe {
  const recipe = harmonizeRecipeColor(raw ?? {})
  if (recipe.paletteId || !recipe.designStyleId) return recipe
  return { ...recipe, paletteId: STYLE_PALETTE_PREFIX + recipe.designStyleId }
}

/**
 * Los colores de «Tu paleta» a partir de la receta de la IA: la paleta elegida,
 * con su giro de tono si lo pidió, y encima los códigos exactos del cliente.
 * Todos los papeles, para que la paleta quede fija aunque se pruebe otra.
 */
export function brandPaletteOf(recipe: StyleRecipe): CustomPalette {
  const style = DESIGN_STYLES.find((item) => item.id === recipe.designStyleId) ?? DESIGN_STYLES[0]!
  const base = paletteWithHue(paletteById(recipe.paletteId) ?? style.palette, recipe.hue ?? 0)
  return { ...(recipe.palette ? paletteWithCustomColors(base, recipe.palette) : base) }
}

/** La receta que usa «Tu paleta»: los colores exactos, sin giro de tono encima. */
export function recipeWithBrand(recipe: StyleRecipe, brand: CustomPalette): StyleRecipe {
  const { color: _color, hue: _hue, ...rest } = recipe
  return { ...rest, palette: brand }
}

export function hydrateForm(draft: AiDraft): CreateForm {
  /* Arranca con «Tu paleta» elegida: la de la IA, o la de los colores que dio
     el cliente. */
  const anchored = anchoredRecipe(draft.aiProject.style)
  const recipe = recipeWithBrand(anchored, brandPaletteOf(anchored))
  const form = hydrateRawForm(draft, recipe)
  /* Sólo se rehace el tema si la receta cambió: si no, el de la IA ya es ése. */
  return recipe === draft.aiProject.style || JSON.stringify(recipe) === JSON.stringify(draft.aiProject.style ?? {})
    ? form
    : { ...form, theme: themeForRecipe(form, recipe) }
}

/** El logo que la IA sacó de las fotos del cliente (`logoPhoto`), o nada. */
function aiLogo(draft: AiDraft): string {
  const business = draft.project.projectContent?.business
  const logo = business && typeof business === 'object' ? (business as { logo?: unknown }).logo : undefined
  return typeof logo === 'string' ? logo : ''
}

function hydrateRawForm(draft: AiDraft, recipe: StyleRecipe): CreateForm {
  const elements = (draft.project.pages[0]?.elements ?? []) as unknown as ElementNode[]
  const theme = draft.project.projectTheme as unknown as ProjectTheme
  const contact = ((draft.project.projectContent?.contact ?? {}) as { phone?: string })
  return {
    // El nombre que eligió la IA, ya puesto: el usuario sólo lo cambia si quiere.
    pageName: draft.aiProject.business.name || draft.aiProject.projectName || '',
    slug: '',
    slugTouched: false,
    companyName: draft.aiProject.business.name || '',
    /* Si entre sus fotos venía su logo, la IA lo reconoció y ya va puesto:
       el paso del nombre lo enseña y se cambia o se quita ahí. */
    logoUrl: aiLogo(draft),
    /* Lo que el cliente dio en su pedido —un teléfono, una dirección, su
       Instagram—: el paso de contacto llega relleno y se puede cambiar. */
    contacts: aiContacts(draft) ?? (contact.phone ? [{ id: 'phone-ai', kind: 'phone', value: contact.phone }] : []),
    hours: normalizeBusinessHours(draft.project.aiGeneration?.hours),
    locations: aiLocations(draft),
    ...(draft.project.aiGeneration && 'portrait' in draft.project.aiGeneration ? { portrait: draft.project.aiGeneration.portrait ?? null } : {}),
    recipe,
    aiRecipe: recipe,
    aiPaletteId: recipe.paletteId,
    brandPalette: brandPaletteOf(recipe),
    aiPalette: brandPaletteOf(recipe),
    theme,
    sections: draft.sections ?? [],
    extraPages: draft.project.pages.slice(1).map((page, at) => ({
      index: at + 1,
      name: typeof page.name === 'string' ? page.name : '',
      slug: typeof page.slug === 'string' ? page.slug : '',
      baseElements: (page.elements ?? []) as ElementNode[],
      sections: draft.pageSections?.[at + 1] ?? [],
      disabledSections: [],
      included: true,
    })),
    exampleContent: Boolean(draft.project.aiGeneration?.exampleContent),
    features: draft.project.aiGeneration?.features ?? draft.aiProject.features ?? [],
    trimmed: draft.project.aiGeneration?.trimmed ?? { pages: 0, sections: 0 },
    language: draft.project.aiGeneration?.language === 'es' ? 'es' : 'en',
    baseElements: elements,
    disabledSections: [],
    sectionImages: {},
    textEdits: {},
    aiBusinessName: draft.aiProject.business.name || '',
    base: draft.project,
  }
}

// ─── estilo y secciones, sin IA ─────────────────────────────────────────────────

/**
 * El tema de una receta, desde cero —como lo hizo el generador— y con el logo
 * que ya tenía el sitio: cambiar de estilo no borra la marca.
 */
export function themeForRecipe(form: CreateForm, recipe: StyleRecipe): ProjectTheme {
  const base = form.baseTheme ? form.baseTheme as unknown as ThemeProjectTheme : normalizeProjectTheme(DEFAULT_PAGE_THEME)
  const next = applyStyleRecipe(base, recipe)
  const logo = (form.theme as unknown as { brandLogo?: unknown }).brandLogo
  return { ...next, ...(logo ? { brandLogo: logo } : {}) } as unknown as ProjectTheme
}

/** El nodo tal cual, con su lista de hijos siempre presente (como deja `remapIds`), sin cambiarle el id. */
function withChildren(node: ElementNode): ElementNode {
  return { ...node, children: (node.children ?? []).map(withChildren) }
}

function remapIds(node: ElementNode): ElementNode {
  return {
    ...node,
    id: crypto.randomUUID(),
    schemaId: node.schemaId ?? node.id,
    children: (node.children ?? []).map(remapIds),
  }
}

const cloneNode = (node: ElementNode): ElementNode => remapIds(JSON.parse(JSON.stringify(node)) as ElementNode)

function imagesIn(nodes: ElementNode[]): string[] {
  const out: string[] = []
  const walk = (node: ElementNode) => {
    if (node.type === 'image' && node.src) out.push(node.src)
    ;(node.children ?? []).forEach(walk)
  }
  nodes.forEach(walk)
  return out
}

/** Cuántas variantes se ofrecen por sección, la original incluida: más era recorrer sin decidir. */
export const MAX_SECTION_VARIANTS = 5

/** Las variantes de una sección en su orden fijo: la original es la primera, y como mucho cinco. */
export function sectionVariants(section: AiDraftSection): { id: string; name: string; thumbnailUrl: string | null }[] {
  const all = section.variants ?? [{ id: section.templateId, name: '', thumbnailUrl: section.thumbnailUrl ?? null }, ...section.alternatives]
  const shown = all.slice(0, MAX_SECTION_VARIANTS)
  /* La puesta siempre está, aunque fuera más allá de las cinco (un borrador de antes). */
  const current = all.find((item) => item.id === section.templateId)
  return current && !shown.includes(current) ? [...shown.slice(0, MAX_SECTION_VARIANTS - 1), current] : shown
}

/** La variante ya vista, tal como quedó; sin ella hay que descargar el template. */
export function visitedVariant(section: AiDraftSection, variantId: string): ElementNode[] | null {
  const nodes = section.visited?.[variantId] as ElementNode[] | undefined
  return nodes?.length ? nodes : null
}

/**
 * Otra variante de una sección: el template nuevo con LOS MISMOS textos —se
 * reparten en sus slots— y las mismas fotos. Sin llamar a la IA.
 *
 * La que se deja se guarda tal cual (`visited`): volver a ella —a la original
 * también— la devuelve como estaba, con sus fotos y lo que se le cambió, en vez
 * de rehacerla desde el template. Por eso `templateElements` puede ir vacío
 * cuando la variante ya se vio.
 */
export function swapSectionVariant(form: CreateForm, index: number, variantId: string, templateElements: ElementNode[], page = 0): CreateForm {
  const slice = pageSlice(form, page)
  const section = slice.sections[index]
  if (!section || variantId === section.templateId) return form
  const variants = sectionVariants(section)
  const alternative = variants.find((item) => item.id === variantId)
  if (!alternative) return form
  const oldRoots = slice.baseElements.filter((node) => section.rootIds.includes(node.id))
  const position = slice.baseElements.findIndex((node) => section.rootIds.includes(node.id))
  if (position < 0) return form
  const seen = visitedVariant(section, variantId)
  const built = seen ?? buildVariant(slice, section, oldRoots, templateElements, form)
  if (!built.length) return form
  /* De qué bloque sale ahora (ver «Cambiar diseño» en el editor). */
  const next = seen ? built : built.map((node, at) => (at === 0 ? { ...node, sourceTemplate: { id: alternative.id, category: section.category } } as ElementNode : node))

  const baseElements = slice.baseElements.filter((node) => !section.rootIds.includes(node.id))
  baseElements.splice(position, 0, ...next)
  const visited = { ...section.visited, [section.templateId]: oldRoots }
  const sections = slice.sections.map((item, at) => at === index
    ? {
        ...item,
        rootIds: next.map((node) => node.id),
        templateId: alternative.id,
        thumbnailUrl: alternative.thumbnailUrl,
        variants,
        alternatives: variants.filter((entry) => entry.id !== alternative.id),
        visited,
      }
    : item)
  return withPageSlice(form, page, { baseElements, sections, disabledSections: slice.disabledSections.filter((id) => !section.rootIds.includes(id)) })
}

/** La variante hecha desde su template: los textos y las fotos de la de antes. */
function buildVariant(form: PageSlice, section: AiDraftSection, oldRoots: ElementNode[], templateElements: ElementNode[], site?: Pick<CreateForm, 'portrait' | 'companyName' | 'aiBusinessName'>): ElementNode[] {
  if (!templateElements.length) return []
  const next = templateElements.map(cloneNode)
  /* Lo mismo que hace el worker al crear (`buildGeneratedSite`): el blog, las
     entradas que se van a crear (antes del reparto); la tienda, sólo su
     familia de ofertas. */
  if (section.seededBlog) bindArticlesToBlog(next as unknown as CopyNode[], () => crypto.randomUUID(), section.copy.language ?? 'en')
  /* Los huecos de contacto, marcados ANTES de los textos, como al crear: si
     no, el reparto los borraba como texto de muestra y la sección de contacto
     quedaba vacía. Sin datos aquí: el paso de contacto los rellena
     (`withContactCards`), y los que falten quedan «por completar». */
  const language = section.copy.language === 'es' ? 'es' : 'en'
  markContactSlots(next as unknown as CopyNode[], [], { language })
  applySectionCopy(next as unknown as CopyNode[], section.copy, (node) => cloneNode(node as unknown as ElementNode) as unknown as CopyNode)
  if (section.category === 'contact') markContactCards(next as unknown as CopyNode[], [], { language })
  if (section.offers) targetOffersKind(next as unknown as CopyNode[], section.offers)
  // El ancla y el fondo de la sección siguen siendo suyos.
  const oldRoot = oldRoots[0]
  if (oldRoot && next[0]) {
    next[0] = {
      ...next[0],
      ...(oldRoot.anchorId ? { anchorId: oldRoot.anchorId } : {}),
      ...(oldRoot.backgroundRole ? { backgroundRole: oldRoot.backgroundRole } : {}),
      styleRefs: { ...next[0].styleRefs, ...(oldRoot.styleRefs?.colors ? { colors: oldRoot.styleRefs.colors } : {}) },
    }
  }
  /* Las fotos: primero las de la variante de antes y, si la nueva tiene más
     huecos, las demás del sitio que aún no salen aquí. Repetir sólo si no hay
     más: una variante de cuatro fotos que venía de una de una enseñaba la misma
     cuatro veces. Los retratos y logos se quedan con las suyas, como al crear
     (`assignImages` en el worker): una foto de producto no sirve de avatar. */
  const own = imagesIn(oldRoots)
  const photos = [...new Set([...own, ...imagesIn(form.baseElements.filter((node) => !section.rootIds.includes(node.id)))])]
  if (photos.length) {
    let cursor = 0
    const walk = (node: ElementNode): ElementNode => {
      const slot = typeof node.aiSlot === 'string' ? node.aiSlot : ''
      const keeps = slot === 'media.logo' || slot.startsWith('media.avatar')
      return {
        ...node,
        ...(node.type === 'image' && !keeps ? { src: photos[cursor++ % photos.length] } : {}),
        children: (node.children ?? []).map(walk),
      }
    }
    next.splice(0, next.length, ...next.map(walk))
  }
  /* El retrato del negocio, como al crear: el que eligió el worker, o el de la
     variante de antes en un borrador sin ese dato; sin ninguno, fuera. Antes
     la variante nueva traía su retrato de muestra (2026-10-30). */
  if (PORTRAIT_CATEGORIES.has(section.category)) {
    const url = site && 'portrait' in site && site.portrait !== undefined ? site.portrait : businessPortraitIn(oldRoots as unknown as CopyNode[])
    placeBusinessPortrait(next as unknown as CopyNode[], url ? { url } : null, site?.companyName || site?.aiBusinessName || '')
  }
  return carryActions(oldRoots, next)
}

/**
 * Los destinos de los botones, de la variante de antes a la nueva. El worker
 * los cablea al crear (`buildGeneratedSite`: contacto, tienda, siguiente
 * sección…), pero la variante nueva sale del template y traía su `#` o su
 * «/about» de muestra: «Conoce más» no llevaba a ninguna parte. Cada botón
 * toma el del mismo slot (`action.primary`, `action.secondary`); los demás, el
 * principal de la sección. Sin ninguno antes, se quedan como vienen.
 */
type LooseNode = ElementNode & { form?: unknown; behavior?: unknown; contactSlot?: unknown; interaction?: { action?: string; href?: string; cmsLink?: unknown } }
const isAction = (node: LooseNode) => (node.type === 'button' || node.type === 'link')
  && !node.form && !node.behavior && !node.contactSlot && !node.interaction?.cmsLink && node.interaction?.action !== 'submit'
const slotBase = (node: ElementNode) => (typeof node.aiSlot === 'string' ? node.aiSlot.trim().replace(/\.\d+$/, '') : '')

export function carryActions(oldRoots: readonly ElementNode[], next: ElementNode[]): ElementNode[] {
  const bySlot = new Map<string, NonNullable<LooseNode['interaction']>>()
  const kept = new Set<string>()
  let main: NonNullable<LooseNode['interaction']> | null = null
  let bySlotPrimary = false
  const read = (node: LooseNode) => {
    const href = node.interaction?.href
    if (isAction(node) && href && href !== '#') {
      const base = slotBase(node)
      if (base && !bySlot.has(base)) bySlot.set(base, node.interaction!)
      // El principal: el del botón principal; si no lo hay, el primero.
      if (base === 'action.primary' ? !bySlotPrimary : !main) main = node.interaction!
      if (base === 'action.primary') bySlotPrimary = true
      kept.add(href)
    }
    ;(node.children ?? []).forEach((child) => read(child as LooseNode))
  }
  oldRoots.forEach((node) => read(node as LooseNode))
  if (!main) return next
  const fallback: NonNullable<LooseNode['interaction']> = main
  const write = (node: LooseNode): ElementNode => {
    const children = (node.children ?? []).map((child) => write(child as LooseNode))
    if (!isAction(node)) return { ...node, children }
    const href = node.interaction?.href ?? ''
    const target = bySlot.get(slotBase(node)) ?? (href && kept.has(href) ? null : fallback)
    return target ? { ...node, interaction: { ...node.interaction, action: target.action ?? (target.href?.startsWith('#') ? 'anchor' : 'link'), href: target.href ?? '' }, children } : { ...node, children }
  }
  return next.map((node) => write(node as LooseNode))
}

/** Lleva una sección de un lugar a otro (arrastrar), paso a paso. */
export function moveSectionTo(form: CreateForm, from: number, to: number, page = 0): CreateForm {
  let next = form
  const step = to > from ? 1 : -1
  for (let at = from; at !== to; at += step) next = moveSection(next, at, step, page)
  return next
}

/** Sube o baja una sección de una página (0 = la portada), con todos sus nodos. */
export function moveSection(form: CreateForm, index: number, delta: -1 | 1, page = 0): CreateForm {
  const slice = pageSlice(form, page)
  const target = index + delta
  const a = slice.sections[index]
  const b = slice.sections[target]
  if (!a || !b) return form
  const groupOf = (section: AiDraftSection) => slice.baseElements.filter((node) => section.rootIds.includes(node.id))
  const first = delta === 1 ? a : b
  const second = delta === 1 ? b : a
  const start = slice.baseElements.findIndex((node) => first.rootIds.includes(node.id) || second.rootIds.includes(node.id))
  if (start < 0) return form
  const without = slice.baseElements.filter((node) => !a.rootIds.includes(node.id) && !b.rootIds.includes(node.id))
  without.splice(start, 0, ...groupOf(second), ...groupOf(first))
  const sections = [...slice.sections]
  sections[index] = b
  sections[target] = a
  return withPageSlice(form, page, { ...slice, baseElements: without, sections })
}
